import { BaseIntegration } from '../../core/BaseIntegration';
import type { IntegrationConfig, IntegrationResult, IntegrationError, IntegrationParams } from '../../types';
import { registerIntegration } from '../../registry';
import {
  LLMClient,
  getAvailableProvider,
  type LLMCallOptions,
  type LLMProvider,
} from '@almadar/llm';
import { z } from 'zod';

/**
 * LLM integration using @almadar/llm
 *
 * Provides 4 actions:
 * - generate: Generate text content from a prompt
 * - classify: Classify text into predefined categories
 * - extract: Extract structured data from text using a schema
 * - summarize: Summarize long text content
 */
export class LLMIntegration extends BaseIntegration {
  private client: LLMClient | null = null;
  private provider: LLMProvider;

  constructor(config: IntegrationConfig) {
    super(config);

    // Determine provider from config or auto-detect from available API keys
    const configuredProvider = config.env.PROVIDER as LLMProvider | undefined;
    this.provider = configuredProvider || getAvailableProvider() || 'anthropic';

    this.logger.info(`LLM integration initialized (provider: ${this.provider})`);
  }

  /**
   * Lazily create client — avoids throwing on construction if API key is missing.
   * The client will throw a clear error when actually used without a key.
   */
  private getClient(): LLMClient {
    if (!this.client) {
      this.client = new LLMClient({
        provider: this.provider,
        temperature: 0.7,
        trackTokens: true,
      });
    }
    return this.client;
  }

  async execute(
    action: string,
    params: IntegrationParams,
  ): Promise<IntegrationResult> {
    // Validate params against registry
    const validation = this.validateParams(action, params);
    if (!validation.valid) {
      return {
        success: false,
        error: {
          name: 'IntegrationError',
          message: 'Validation failed',
          code: 'VALIDATION_ERROR',
          details: validation.errors,
        } as IntegrationError,
        metadata: this.createMetadata(action, 0),
      };
    }

    const startTime = Date.now();

    try {
      let data: unknown;

      switch (action) {
        case 'generate':
          data = await this.executeWithRetry(() => this.generate(params));
          break;
        case 'classify':
          data = await this.executeWithRetry(() => this.classify(params));
          break;
        case 'extract':
          data = await this.executeWithRetry(() => this.extract(params));
          break;
        case 'summarize':
          data = await this.executeWithRetry(() => this.summarize(params));
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      return {
        success: true,
        data,
        metadata: this.createMetadata(action, Date.now() - startTime),
      };
    } catch (error) {
      return this.handleError(action, error);
    }
  }

  private async generate(params: IntegrationParams) {
    const {
      systemPrompt,
      userPrompt,
      model,
      temperature,
      maxTokens,
    } = params as {
      systemPrompt?: string;
      userPrompt: string;
      model?: string;
      temperature?: number;
      maxTokens?: number;
    };

    this.logger.debug('Generating content', { model, temperature });

    const client = model
      ? new LLMClient({ provider: this.provider, model, temperature: temperature ?? 0.7 })
      : this.getClient();

    const response = await client.callWithMetadata<string>({
      systemPrompt: systemPrompt || 'You are a helpful assistant.',
      userPrompt,
      maxTokens: maxTokens ?? 1024,
      temperature,
      skipSchemaValidation: true,
    });

    return {
      content: response.data,
      usage: response.usage
        ? {
            promptTokens: response.usage.promptTokens,
            completionTokens: response.usage.completionTokens,
            totalTokens: response.usage.totalTokens,
          }
        : { tokens: 0 },
    };
  }

  private async classify(params: IntegrationParams) {
    const { text, categories, model } = params as {
      text: string;
      categories: string[];
      model?: string;
    };

    this.logger.debug('Classifying text', { categories: categories.join(', ') });

    const ClassificationSchema = z.object({
      category: z.enum(categories as [string, ...string[]]),
      confidence: z.number().min(0).max(1),
      reasoning: z.string(),
    });

    const client = model
      ? new LLMClient({ provider: this.provider, model, temperature: 0.1 })
      : this.getClient();

    const result = await client.call({
      systemPrompt: `You are a text classifier. Classify the given text into one of these categories: ${categories.join(', ')}. Return JSON with: category, confidence (0-1), and reasoning.`,
      userPrompt: text,
      schema: ClassificationSchema,
      temperature: 0.1,
    });

    return result;
  }

  private async extract(params: IntegrationParams) {
    const { text, schema, model } = params as {
      text: string;
      schema: IntegrationParams;
      model?: string;
    };

    this.logger.debug('Extracting structured data', { schema: String(schema ?? '') });

    // Build a Zod schema from the JSON schema description.
    // For complex schemas, we use z.record to accept any shape and let the LLM
    // follow the schema description in the prompt.
    const ExtractSchema = z.record(z.unknown());

    const client = model
      ? new LLMClient({ provider: this.provider, model, temperature: 0.2 })
      : this.getClient();

    const schemaDescription = JSON.stringify(schema, null, 2);

    const result = await client.call({
      systemPrompt: `You are a structured data extractor. Extract data from the given text according to this schema:\n\n${schemaDescription}\n\nReturn ONLY valid JSON matching the schema.`,
      userPrompt: text,
      schema: ExtractSchema,
      temperature: 0.2,
      skipSchemaValidation: true,
    });

    return result;
  }

  private async summarize(params: IntegrationParams) {
    const { text, maxLength, style, model } = params as {
      text: string;
      maxLength?: number;
      style?: string;
      model?: string;
    };

    this.logger.debug('Summarizing text', { maxLength, style });

    const SummarySchema = z.object({
      summary: z.string(),
      keyPoints: z.array(z.string()),
    });

    const styleInstructions = style === 'bullet'
      ? 'Use bullet points.'
      : style === 'detailed'
        ? 'Be thorough and detailed.'
        : 'Be concise.';

    const lengthInstructions = maxLength
      ? `Keep the summary under ${maxLength} words.`
      : '';

    const client = model
      ? new LLMClient({ provider: this.provider, model, temperature: 0.3 })
      : this.getClient();

    const result = await client.call({
      systemPrompt: `You are a text summarizer. ${styleInstructions} ${lengthInstructions} Return JSON with: summary (string) and keyPoints (array of strings).`,
      userPrompt: text,
      schema: SummarySchema,
      temperature: 0.3,
    });

    return result;
  }
}

// Auto-register
registerIntegration('llm', LLMIntegration);
